import cls from 'classnames';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import React, { useContext, useEffect, useMemo, useState } from 'react';

import Call from '@/components/Icon/Call';
import Chat from '@/components/Icon/Chat';
import Email from '@/components/Icon/Email';
import Location from '@/components/Icon/Location';
import Title from '@/components/Title';
import { MyContext } from '@/config/context-manager';
import { getPhoneNumber } from '@/request/common';
import { GetIpInfo } from '@/request/independent-appointment';
import Addrees1enImg from '@public/images/contactUs/addrees-1-en.png';
import Addrees1zhImg from '@public/images/contactUs/addrees-1-zh.png';
import Addrees2enImg from '@public/images/contactUs/addrees-2-en.png';

interface IList {
  icon: React.ReactNode;
  title: string;
  description: React.ReactNode;
}
const OlineReference = () => {
  const { t } = useTranslation('contactUs');
  const { locale } = useContext(MyContext);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isChina, setIsChina] = useState(false);
  useEffect(() => {
    getPhoneNumber().then(res => {
      setPhoneNumber(res.content);
    });
    GetIpInfo().then(res => {
      setIsChina(res.content.country === 'China');
    });
  }, []);

  const openIntercom = () => {
    if (window?.Intercom) {
      // Intercom 已初始化，调用其方法
      window?.Intercom?.('show');
      return true;
    }
    return false;
  };

  const list: IList[] = useMemo(
    () => [
      {
        icon: <Email />,
        title: t('邮箱地址'),
        description: t('邮箱'),
      },
      {
        icon: <Call />,
        title: t('电话号码'),
        description: phoneNumber,
      },
      {
        icon: <Chat />,
        title: t('在线咨询'),
        description: (
          <span
            onClick={openIntercom}
            className="cursor-pointer text-[#453DEE]">
            {t('立即沟通')}
          </span>
        ),
      },
    ],
    [t, phoneNumber],
  );

  const mapList = useMemo(
    () => [
      {
        key: '1',
        title: t('美国地址-标题'),
        description: t('美国地址-详细地址'),
        image: locale === 'en' ? Addrees1enImg : Addrees1zhImg,
        link: 'https://maps.app.goo.gl/KwmtvagKdhN1nBF9A',
      },
      {
        key: '2',
        title: t('新西兰地址-标题'),
        description: t('新西兰地址-详细地址'),
        image: Addrees2enImg,
        link: 'https://maps.app.goo.gl/eayDyzNfFiqXisCAA',
      },
    ],
    [locale, t],
  );

  return (
    <div className="">
      <Title theme="colorful">
        <span
          className="[&_strong]:inline"
          dangerouslySetInnerHTML={{ __html: t('slogan') }}
        />
      </Title>
      <div className="mt-6 flex flex-col space-y-6 lg:mx-0 lg:mt-12 lg:flex-row lg:space-x-4 lg:space-y-0 xl:mt-15 xl:space-x-6">
        {list.map(item => (
          <div
            key={item.title}
            className="flex w-full flex-col items-center justify-center space-y-3 rounded-3xl border-2 border-white/45 bg-gradient-to-t from-white/40 to-white/90 px-5 py-6 shadow-[0px_8px_30px_0px_rgba(0,0,0,0.06)] backdrop-blur lg:w-1/3 lg:space-y-4 lg:py-8 xl:space-y-5 xl:rounded-5xl xl:px-8 xl:py-10">
            <div
              style={{
                background:
                  'linear-gradient(94deg, rgba(156, 65, 199, 0.06) 0.44%, rgba(63, 100, 253, 0.06) 44.33%, rgba(2, 156, 255, 0.06) 97%)',
              }}
              className="flex h-12 w-12 items-center justify-center rounded-full bg-white text-wk-words-2 lg:h-15 lg:w-15 xl:h-[72px] xl:w-[72px]">
              {item.icon}
            </div>
            <h5 className="text-[20px] font-bold lg:text-[24px] xl:text-[28px]">
              {item.title}
            </h5>
            <p className="text-[16px] text-wk-words-2 xl:text-[20px]">
              {item.description}
            </p>
          </div>
        ))}
      </div>
      <div className="mt-5 flex flex-col justify-stretch space-y-5 rounded-3xl border-2 border-white/45 bg-gradient-to-t from-white/40 to-white/90 p-5 shadow-[0px_8px_30px_0px_rgba(0,0,0,0.06)] backdrop-blur lg:mt-4 lg:flex-row lg:space-x-5 lg:space-y-0 xl:mt-8 xl:space-x-8 xl:rounded-5xl xl:p-8">
        {mapList.map(item => (
          <div
            key={item.key}
            className="flex flex-1 flex-col items-stretch justify-between">
            <div className="mb-3 flex items-start space-x-3 lg:mb-5 lg:space-x-6 xl:mb-6">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[99px]  bg-[linear-gradient(94deg,rgba(156,65,199,0.06)_0.44%,rgba(63,100,253,0.06)_44.33%,rgba(2,156,255,0.06)_97%)] text-wk-words-2 lg:h-15 lg:w-15 xl:h-[72px] xl:w-[72px]">
                <Location />
              </div>
              <div className="space-y-2">
                <h5 className="text-xl font-bold lg:text-2xl xl:text-[28px]">
                  {item.title}
                </h5>
                <p className="text-[14px] text-wk-words-2 lg:text-[16px] xl:text-xl">
                  {item.description}
                </p>
              </div>
            </div>
            <Image
              onClick={() => {
                if (isChina) {
                  return;
                }
                window.open(item.link, '_blank');
              }}
              src={item.image}
              alt={item.title}
              className={cls(
                !isChina && 'cursor-pointer',
                'h-[110px] lg:h-[148px] xl:h-[200px]',
              )}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default OlineReference;
